import { ICurrencyEntityReference, IPhotoEntityReference, PictureDisplayMode } from '../../../basic';
import { IUiButtonSettings } from '../../../configs/ui-elements';
import { GsStructComponent, GsStructPageItems } from '../component';
import { CustomerLoyaltyMode } from './debit-card-detail-component';
import { IMethod } from './../basic/method';
import { IPaginated } from './../basic/paginated';

/** Mirrors the backend enum `VoucherType`. */
export enum VoucherType {
  Discount = 0,
  Voucher = 1,
  Bonus = 2,
  DiscountCoupon = 3
}

/** Mirrors the backend enum `DebitCardVouchersFilter`. */
export enum DebitCardVouchersFilter {
  Activated = 0,
  Devalued = 1,
  NotAvailable = 2,
  Available = 3
}

/** Mirrors the backend enum `DebitCardRewardsFilter`. */
export enum DebitCardRewardsFilter {
  All = 0,
  Reached = 1,
  NotReached = 2
}

/** A single coupon or reward of the member's debit card */
export interface IDebitCardVoucher {
  /** ID of the voucher, used by the activate and deactivate methods */
  voucherID: number;
  /** Display name of the voucher */
  name?: string;
  /** Description of the voucher, usually only filled for rewards */
  description?: string;
  /** Date the voucher is valid from */
  validFrom?: string;
  /** Date the voucher is valid until */
  validTo?: string;
  /** Main photo of the voucher */
  photo?: IPhotoEntityReference;
  /** Points needed to reach the voucher, only filled in the points loyalty mode */
  neededPoints?: number;
  /** Turnover needed to reach the voucher, only filled in the turnover loyalty mode */
  neededTurnover?: number;
  /** Currency of `neededTurnover` */
  currency?: ICurrencyEntityReference;
  /** Whether the voucher is currently assigned to the basket */
  activated: boolean;
  /** Whether the voucher has already been redeemed */
  devalued: boolean;
  /** Whether the member reached the needed points or turnover, optional: a missing flag must not lock the entry */
  reached?: boolean;
  /** Whether the voucher can only be redeemed in store, it cannot be activated in the web shop */
  noOnline?: boolean;
  /** Link to the voucher detail page */
  detailLink?: string;
}

/** Data the backend renders for the DebitCardVouchers component */
export interface IDebitCardVouchersRender {
  /** The vouchers of the currently selected filter */
  items: IPaginated<IDebitCardVoucher>;
  /** Customer loyalty mode the debit card settings are running in */
  loyaltyMode: CustomerLoyaltyMode;
  /** Methods that are given with the HTTP request */
  methods: IDebitCardVouchersMethods;
}

/** HTTP methods for the DebitCardVouchers component */
export interface IDebitCardVouchersMethods {
  /** Loads a page of vouchers, optionally with another filter */
  populate: IMethod;
  /** Assigns a voucher to the basket */
  activate: IMethod;
  /** Removes the activated voucher from the basket */
  deactivate: IMethod;
}

/** Response of the activate and deactivate methods */
export interface IActivateVoucherResponse {
  /** ID of the voucher that is active now, empty after deactivating */
  activatedVoucherID?: number;
}

/** Structure definition for the DebitCardVouchers component. */
export interface GsStructDebitCardVouchersComponent extends GsStructComponent<IDebitCardVouchersRender>, GsStructPageItems {
  /** The specified type-key for this struct */
  componentType: 'DebitCardVouchers';
  /** Whether the component shows coupons or rewards */
  voucherType?: VoucherType;
  /** How the voucher image fills its box */
  pictureDisplayMode?: PictureDisplayMode;
  /** Whether the filter tabs are shown above the list */
  showFilterTabs?: boolean;
  /** The filter the list is restricted to while the filter tabs are hidden */
  filter?: DebitCardVouchersFilter;
  /** Whether the reward list shows all, only reached or only unreached rewards, rewards only */
  rewardsFilter?: DebitCardRewardsFilter;
  /** Button styling overrides for this component */
  buttons?: IUiButtonSettings;
}
